// Session-detail projection (#980): `GET /v1/sessions/:id` — the list row plus
// repo + entity context, feeding the /audit/:session_id PageHeader. Mirrors
// lib/repo-detail: one point lookup over `apiFetch` with the session bearer, kept
// fresh by the account stream's `sessions` topic (keyed by session id): a change
// to THIS session re-reads the header — its outcome, finish and token counts move
// while it runs. An older session past the topic's head polls (`createLiveQuery`).

import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { createLiveQuery } from './live/live-query';
import { SESSION_TOKEN } from './session';

/** The `/v1/sessions/:id` projection — apps/api/src/routes/sessions.ts `projectSessionDetail`. */
export type SessionDetail = {
  readonly id: string;
  readonly title: string;
  readonly trigger: string;
  readonly entity_kind: string;
  readonly outcome: string | null;
  readonly model: string;
  readonly started_iso: string;
  readonly finished_iso: string | null;
  readonly repo_full_name: string;
  readonly entity_number: number | null;
  readonly entity_title: string | null;
  readonly token_in: number;
  readonly token_out: number;
  /**
   * What this session's tokens are WORTH at OpenRouter list rates. NOT a bill and
   * NOT what the run cost — we are BYOK and never charge for tokens. Null unless
   * {@link SessionDetail.reference_basis} is `listed`.
   */
  readonly reference_usd: number | null;
  /** `listed` | `unlisted` | `not_recorded` | `legacy_unattributed`. */
  readonly reference_basis: string;
};

export const SESSION_DETAIL_QUERY_KEY = 'session-detail';

export function useSessionDetail(sessionId: () => string, token: string | null = SESSION_TOKEN) {
  return createLiveQuery<SessionDetail>(
    () => ({
      queryKey: [SESSION_DETAIL_QUERY_KEY, sessionId(), token, activeAccountId()],
      enabled: token !== null,
      live: [{ entity: 'session', id: sessionId() }],
      queryFn: () =>
        apiFetch<SessionDetail>(`/v1/sessions/${encodeURIComponent(sessionId())}`, token),
    }),
    token,
  ).query;
}

/**
 * GitHub URL of the triggering entity, when the session carries one. The
 * projection coalesces issue/PR numbers into `entity_number`; a repo-scoped
 * session (`entity_kind: 'repo'`) carries none, so it yields no link.
 */
export function entityUrl(d: SessionDetail | undefined): string | null {
  if (!d || d.entity_number === null) return null;
  const segment = d.entity_kind === 'pr' ? 'pull' : 'issues';
  return `https://github.com/${d.repo_full_name}/${segment}/${d.entity_number}`;
}
